import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  ChevronLeft,
  Trash2,
  Pencil,
  Check,
  X,
  Tag,
  Crosshair,
  Paperclip,
  Archive,
} from 'lucide-react';
import type { JSONContent } from '@tiptap/react';
import { DossierEditor } from '../components/editor';
import { apiGet, apiPost, apiPatch, apiDelete } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { LinkPicker } from '../components/LinkPicker';
import { FileUpload } from '../components/FileUpload';
import { DropdownPicker } from '../components/DropdownPicker';
import { formatRelativeTime, useNow } from '../lib/relativeTime';
import { useAutosaveDelay } from '../contexts/PreferencesContext';
import { PinButton } from '../components/PinButton';
import { DetailHeader } from '../components/DetailHeader';
import { Backlinks } from '../components/Backlinks';
import { excerptDeleteWarning } from '../lib/excerpts';

/* ── Types ─────────────────────────────────────────────── */

interface NoteDetail {
  id: string;
  title: string;
  content: string | null;
  tag_ids: string[];
  scope_ids: string[];
  created_at: string;
  updated_at: string;
}

interface TagItem {
  id: string;
  name: string;
  color: string;
}

interface ScopeItem {
  id: string;
  name: string;
  color: string | null;
  status?: string;
}

/* ── Main Component ───────────────────────────────────── */

export function NoteDetailPage() {
  const { noteId } = useParams<{ noteId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const confirm = useConfirm();
  const autosaveDelay = useAutosaveDelay();
  const navState = location.state as { from?: string; fromLabel?: string } | null;
  useNow(); // tick every 30s so relative timestamps stay fresh

  const [note, setNote] = useState<NoteDetail | null>(null);
  const [tags, setTags] = useState<TagItem[]>([]);
  const [scopes, setScopes] = useState<ScopeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);

  // Inline editing states
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);

  const [showTagPicker, setShowTagPicker] = useState(false);
  const [showScopePicker, setShowScopePicker] = useState(false);

  /* ── Data Loading ───────────────────────────────────── */

  const loadNote = useCallback(async () => {
    if (!noteId) return;
    setLoading(true);
    setNote(null);
    try {
      const [n, allTags, allScopes] = await Promise.all([
        apiGet<NoteDetail>(`/notes/${noteId}`),
        apiGet<TagItem[]>('/tags'),
        apiGet<ScopeItem[]>('/scopes?include_completed=true'),
      ]);
      setNote(n);
      setTags(allTags);
      setScopes(allScopes);
    } catch (err) {
      console.error('Failed to load note:', err);
    } finally {
      setLoading(false);
    }
  }, [noteId]);

  useEffect(() => { loadNote(); }, [loadNote]);

  /* ── Field Patching ─────────────────────────────────── */

  async function patchNote(updates: Record<string, unknown>) {
    if (!noteId) return;
    setSaving(true);
    try {
      const updated = await apiPatch<NoteDetail>(`/notes/${noteId}`, updates);
      setNote(updated);
      setLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Failed to update note:', err);
      toast.error('Failed to update note');
    } finally {
      setSaving(false);
    }
  }

  /* ── Title Editing ──────────────────────────────────── */

  function startEditTitle() {
    if (!note) return;
    setTitleDraft(note.title);
    setEditingTitle(true);
    setTimeout(() => titleInputRef.current?.focus(), 0);
  }

  function saveTitle() {
    const trimmed = titleDraft.trim();
    if (trimmed && trimmed !== note?.title) {
      patchNote({ title: trimmed });
    }
    setEditingTitle(false);
  }

  function cancelEditTitle() {
    setEditingTitle(false);
  }

  /* ── Content Autosave ───────────────────────────────── */

  const contentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleContentUpdate = useCallback((content: JSONContent) => {
    if (contentTimerRef.current) clearTimeout(contentTimerRef.current);
    contentTimerRef.current = setTimeout(() => {
      patchNote({ content: JSON.stringify(content) });
    }, autosaveDelay);
  }, [noteId]);

  /* ── Tag / Scope Toggling ───────────────────────────── */

  function toggleTag(tagId: string) {
    if (!note) return;
    const newTagIds = note.tag_ids.includes(tagId)
      ? note.tag_ids.filter(id => id !== tagId)
      : [...note.tag_ids, tagId];
    patchNote({ tag_ids: newTagIds });
  }

  function toggleScope(scopeId: string) {
    if (!note) return;
    const newScopeIds = note.scope_ids.includes(scopeId)
      ? note.scope_ids.filter(id => id !== scopeId)
      : [...note.scope_ids, scopeId];
    patchNote({ scope_ids: newScopeIds });
  }

  async function createTag(name: string) {
    try {
      const created = await apiPost<TagItem>('/tags', { name });
      setTags(prev => [...prev, created]);
      return { id: created.id, label: created.name, color: created.color };
    } catch (err) {
      console.error('Failed to create tag:', err);
      toast.error('Failed to create tag');
    }
  }

  async function createScope(name: string) {
    try {
      const created = await apiPost<ScopeItem>('/scopes', { name });
      setScopes(prev => [...prev, created]);
      return { id: created.id, label: created.name, color: created.color ?? undefined };
    } catch (err) {
      console.error('Failed to create project:', err);
      toast.error('Failed to create project');
    }
  }

  /* ── Delete ─────────────────────────────────────────── */

  async function handleDelete() {
    if (!noteId) return;
    const excerptNote = await excerptDeleteWarning('note', noteId);
    if (!await confirm({ title: 'Delete this note?', message: ['This cannot be undone.', excerptNote].filter(Boolean).join(' '), variant: 'danger', confirmLabel: 'Delete' })) return;
    try {
      await apiDelete(`/notes/${noteId}`);
      toast.success('Note deleted');
      navigate('/notes');
    } catch (err) {
      console.error('Failed to delete note:', err);
      toast.error('Failed to delete note');
    }
  }

  /* ── Render ─────────────────────────────────────────── */

  if (loading) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  if (!note) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
      <div className="max-w-4xl mx-auto">
        <Link to={navState?.from ?? '/notes'} className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors mb-4">
          <ChevronLeft size={16} /> {navState?.fromLabel ?? 'Back to Notes'}
        </Link>
        <div className="text-text-muted text-center py-16">Note not found.</div>
      </div>
      </div>
    );
  }

  const tagMap = new Map(tags.map(t => [t.id, t]));
  const scopeMap = new Map(scopes.map(s => [s.id, s]));

  const noteContent: JSONContent | null = note.content
    ? (() => { try { return JSON.parse(note.content); } catch { return null; } })()
    : null;

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
    <div className="max-w-4xl mx-auto">
      <DetailHeader
        back={{ to: navState?.from ?? '/notes', label: navState?.fromLabel ?? 'Back to Notes' }}
        all={navState?.from && navState.from !== '/notes' ? { to: '/notes', label: 'All Notes' } : undefined}
        lastSaved={lastSaved}
        saving={saving}
        actions={[
          {
            key: 'archive', icon: <Archive size={14} />, label: 'Archive',
            onClick: async () => {
              try {
                await apiPatch(`/notes/${noteId}/archive`, {});
                toast.success('Note archived');
                navigate('/notes');
              } catch { toast.error('Failed to archive note'); }
            },
          },
          { kind: 'remind', key: 'remind', entityType: 'note', entityId: noteId!, entityTitle: note?.title ?? 'Note' },
          { key: 'delete', icon: <Trash2 size={14} />, label: 'Delete', onClick: handleDelete, danger: true },
        ]}
      />

      {/* Title */}
      <div className="mb-6">
        {editingTitle ? (
          <div className="flex items-center gap-2">
            <input
              ref={titleInputRef}
              type="text"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveTitle();
                if (e.key === 'Escape') cancelEditTitle();
              }}
              onBlur={saveTitle}
              className="flex-1 text-2xl font-bold bg-bg-input border border-border rounded-md px-3 py-1 text-text-primary focus:outline-none focus:border-accent"
            />
            <button onClick={saveTitle} className="p-1 text-success hover:bg-bg-hover rounded">
              <Check size={20} />
            </button>
            <button onClick={cancelEditTitle} className="p-1 text-text-muted hover:bg-bg-hover rounded">
              <X size={20} />
            </button>
          </div>
        ) : (
          <h1
            className="text-2xl font-bold text-text-primary cursor-pointer hover:text-accent transition-colors group flex items-start gap-2"
            onClick={startEditTitle}
          >
            <span className="min-w-0 [overflow-wrap:anywhere]">{note.title}</span>
            <Pencil size={16} className="flex-shrink-0 mt-2 opacity-0 group-hover:opacity-50 transition-opacity" />
            <PinButton entityType="note" entityId={noteId!} label={note.title} />
          </h1>
        )}
        <div className="flex items-center gap-3 mt-2 text-xs text-text-muted">
          <span>Created {formatRelativeTime(note.created_at)}</span>
          <span>Updated {formatRelativeTime(note.updated_at)}</span>
        </div>
      </div>

      {/* Tags & Projects */}
      <div className="field-grid mb-6 bg-bg-card border border-border rounded-lg p-4">
        {/* Tags */}
        <div className="relative">
          <label className="block text-xs text-text-muted mb-1.5 uppercase tracking-wide flex items-center gap-1">
            <Tag size={10} /> Tags
          </label>
          <div
            className="min-h-[38px] px-3 py-1.5 bg-bg-input border border-border rounded-md flex flex-wrap gap-1.5 items-center cursor-pointer hover:border-border-hover transition-colors"
            onClick={() => setShowTagPicker(!showTagPicker)}
          >
            {note.tag_ids.length === 0 ? (
              <span className="text-sm text-text-muted">Add tags...</span>
            ) : (
              note.tag_ids.map(tid => {
                const tag = tagMap.get(tid);
                return tag ? (
                  <span
                    key={tid}
                    className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1"
                    style={{ backgroundColor: tag.color + '20', color: tag.color }}
                  >
                    <Link
                      to={`/notes?tag=${tid}&tagName=${encodeURIComponent(tag.name)}`}
                      onClick={(e) => e.stopPropagation()}
                      className="hover:underline"
                    >
                      {tag.name}
                    </Link>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleTag(tid); }}
                      className="hover:opacity-70"
                    >
                      <X size={10} />
                    </button>
                  </span>
                ) : null;
              })
            )}
          </div>
          {showTagPicker && (
            <DropdownPicker
              items={tags.map(t => ({ id: t.id, label: t.name, color: t.color }))}
              selectedIds={note.tag_ids}
              onToggle={toggleTag}
              onClose={() => setShowTagPicker(false)}
              onCreate={createTag}
            />
          )}
        </div>

        {/* Scopes / Projects */}
        <div className="relative">
          <label className="block text-xs text-text-muted mb-1.5 uppercase tracking-wide flex items-center gap-1">
            <Crosshair size={10} /> Projects
          </label>
          <div
            className="min-h-[38px] px-3 py-1.5 bg-bg-input border border-border rounded-md flex flex-wrap gap-1.5 items-center cursor-pointer hover:border-border-hover transition-colors"
            onClick={() => setShowScopePicker(!showScopePicker)}
          >
            {note.scope_ids.length === 0 ? (
              <span className="text-sm text-text-muted">Add projects...</span>
            ) : (
              note.scope_ids.map(sid => {
                const scope = scopeMap.get(sid);
                return scope ? (
                  <span
                    key={sid}
                    className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1 bg-bg-hover text-text-secondary"
                    style={scope.color ? { backgroundColor: scope.color + '20', color: scope.color } : undefined}
                  >
                    {scope.name}
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleScope(sid); }}
                      className="hover:opacity-70"
                    >
                      <X size={10} />
                    </button>
                  </span>
                ) : null;
              })
            )}
          </div>
          {showScopePicker && (
            <DropdownPicker
              items={scopes.filter(s => s.status !== 'completed' || note.scope_ids.includes(s.id)).map(s => ({ id: s.id, label: s.name, color: s.color ?? undefined }))}
              selectedIds={note.scope_ids}
              onToggle={toggleScope}
              onClose={() => setShowScopePicker(false)}
              onCreate={createScope}
            />
          )}
        </div>
      </div>

      {/* Related Items */}
      <div className="mb-6 bg-bg-card border border-border rounded-lg p-4">
        <LinkPicker
          sourceType="note"
          sourceId={note.id}
          relationship="related"
          allowedTargetTypes={['task', 'meeting_instance', 'meeting_series', 'reference_doc', 'person', 'note']}
          label="Related Items"
          placeholder="Link task, meeting, person, doc..."
        />
      </div>

      {/* Content Editor */}
      <div className="mb-8">
        <label className="block text-xs text-text-muted mb-2 uppercase tracking-wide">Content</label>
        <DossierEditor
          key={noteId}
          content={noteContent}
          onUpdate={handleContentUpdate}
          editable={true}
          placeholder="Start writing..."
          excerptHost={noteId ? { type: 'note', id: noteId } : undefined}
        />
      </div>

      {/* Attachments */}
      <div className="mb-8">
        <label className="flex items-center gap-1.5 text-xs text-text-muted mb-2 uppercase tracking-wide">
          <Paperclip size={12} /> Attachments
        </label>
        <FileUpload parentType="note" parentId={note.id} />
      </div>

      {/* Backlinks */}
      <Backlinks entityType="note" entityId={note.id} className="mb-8" />
    </div>
    </div>
  );
}

/* ── Utility Functions ────────────────────────────────── */

